import React, { useState, useEffect } from "react";
import { Loader, Label } from "semantic-ui-react";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixGriform, EnvironmentalRiskMatrixIfrsform } from "../../../../api";
import "./EsrsDoubleMateriality.scss";

const environmentalRiskMatrixGriCtrl = new EnvironmentalRiskMatrixGriform();
const environmentalRiskMatrixIfrsCtrl = new EnvironmentalRiskMatrixIfrsform();

const MATERIALITY_THRESHOLD = 3; // ESRS 1: material si >=3/5 en cualquiera de los dos ejes (umbral configurable)

// ESRS no tiene carga propia (fields:[] en el prototipo): integra
// automáticamente lo que ya se cargó en GRI (eje impacto, severity 1-5) e
// IFRS S2 (eje financiero, severity 1-5), agrupando por título — un "tema"
// es el mismo texto de título cargado desde uno o ambos estándares.
function buildThemes(griItems, ifrsItems) {
  const byTitle = new Map();

  const upsert = (title, side, value) => {
    const key = (title || "").trim().toLowerCase();
    if (!key) return;
    if (!byTitle.has(key)) byTitle.set(key, { title: title.trim(), imp: null, fin: null });
    byTitle.get(key)[side] = value;
  };

  griItems.forEach((item) => upsert(item.title, "imp", item.severity));
  ifrsItems.forEach((item) => upsert(item.title, "fin", item.severity));

  return Array.from(byTitle.values()).map((theme) => {
    const impMaterial = (theme.imp || 0) >= MATERIALITY_THRESHOLD;
    const finMaterial = (theme.fin || 0) >= MATERIALITY_THRESHOLD;
    let origin = "both";
    if (theme.imp == null) origin = "ifrs";
    else if (theme.fin == null) origin = "gri";
    return { ...theme, impMaterial, finMaterial, origin, isMaterial: impMaterial || finMaterial };
  });
}

export function EsrsDoubleMateriality({ siteSelected, reload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [themes, setThemes] = useState(null);

  useEffect(() => {
    (async () => {
      if (!siteSelected) {
        setThemes([]);
        return;
      }
      try {
        const [griRes, ifrsRes] = await Promise.all([
          environmentalRiskMatrixGriCtrl.getEnvironmentalRiskMatricesGriBySite(accessToken, siteSelected),
          environmentalRiskMatrixIfrsCtrl.getEnvironmentalRiskMatricesIfrsBySite(accessToken, siteSelected),
        ]);
        const griItems = griRes.code === 200 ? griRes.riskMatrices || [] : [];
        const ifrsItems = ifrsRes.code === 200 ? ifrsRes.riskMatrices || [] : [];
        setThemes(buildThemes(griItems, ifrsItems));
      } catch (error) {
        console.error(error);
        setThemes([]);
      }
    })();
  }, [siteSelected, reload, accessToken]);

  if (!themes) return <Loader active inline="centered" />;

  if (themes.length === 0) {
    return <p className="esrs-dm__empty">{t("risk_matrix_esrs_empty")}</p>;
  }

  const materialCount = themes.filter((th) => th.isMaterial).length;
  const doubleCount = themes.filter((th) => th.impMaterial && th.finMaterial).length;

  const quadrants = [
    { key: "double", labelKey: "risk_matrix_esrs_quadrant_double", items: themes.filter((th) => th.impMaterial && th.finMaterial) },
    { key: "impact", labelKey: "risk_matrix_esrs_quadrant_impact", items: themes.filter((th) => th.impMaterial && !th.finMaterial) },
    { key: "financial", labelKey: "risk_matrix_esrs_quadrant_financial", items: themes.filter((th) => !th.impMaterial && th.finMaterial) },
    { key: "none", labelKey: "risk_matrix_esrs_quadrant_none", items: themes.filter((th) => !th.impMaterial && !th.finMaterial) },
  ];

  const originLabel = (origin) =>
    origin === "gri" ? t("risk_matrix_esrs_origin_gri") : origin === "ifrs" ? t("risk_matrix_esrs_origin_ifrs") : t("risk_matrix_esrs_origin_both");

  return (
    <div className="esrs-dm">
      <p className="esrs-dm__sub">{t("risk_matrix_esrs_dm_sub")}</p>

      <div className="esrs-dm__stats">
        <span className="esrs-dm__stat"><b>{themes.length}</b> {t("risk_matrix_esrs_stat_themes")}</span>
        <span className="esrs-dm__stat"><b>{materialCount}</b> {t("risk_matrix_esrs_stat_material")}</span>
        <span className="esrs-dm__stat"><b>{doubleCount}</b> {t("risk_matrix_esrs_stat_double")}</span>
      </div>

      <h3>{t("risk_matrix_esrs_quadrant_title")}</h3>
      <div className="esrs-dm__quadrants">
        {quadrants.map((q) => (
          <div key={q.key} className={`esrs-dm__quadrant esrs-dm__quadrant--${q.key}`}>
            <div className="esrs-dm__quadrant-title">
              {t(q.labelKey)} <span className="esrs-dm__quadrant-count">{q.items.length}</span>
            </div>
            <ul>
              {q.items.map((th) => (
                <li key={th.title}>{th.title}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h3>{t("risk_matrix_esrs_themes_title")}</h3>
      <div className="esrs-dm__scrollx">
        <table className="esrs-dm__table">
          <thead>
            <tr>
              <th>{t("risk_matrix_esrs_col_theme")}</th>
              <th>{t("risk_matrix_esrs_col_origin")}</th>
              <th>{t("risk_matrix_esrs_col_impact_mat")}</th>
              <th>{t("risk_matrix_esrs_col_financial_mat")}</th>
              <th>{t("risk_matrix_esrs_col_is_material")}</th>
            </tr>
          </thead>
          <tbody>
            {themes.map((th) => (
              <tr key={th.title}>
                <td>{th.title}</td>
                <td>{originLabel(th.origin)}</td>
                <td>{th.imp ?? "—"}</td>
                <td>{th.fin ?? "—"}</td>
                <td>
                  <Label basic={!th.isMaterial} color={th.isMaterial ? "red" : "grey"}>
                    {th.isMaterial ? t("risk_matrix_esrs_yes") : t("risk_matrix_esrs_no")}
                  </Label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
